"use client";

import { Check, Copy } from "lucide-react";
import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  checkGrammar,
  type GrammarFinding,
  type GrammarReport,
} from "@/lib/grammar-check";
import { t, tPlural } from "@/lib/i18n/interpolate";

const SAMPLE =
  "Yesterday i finished the the first draft. The results was a interesting finding ,and we should of expected it. This matters alot — more then we thought. it was definately worth the effort.";

/** English strings. A translated content file overrides these through `ui`. */
export const GRAMMAR_CHECKER_STRINGS = {
  highConfidence: "High confidence",
  worthALook: "Worth a look",
  copied: "Copied",
  copyFailed: "Copy failed",
  copyFix: "Copy fix",
  wordsCount: "one:{count} word|other:{count} words",
  noIssuesDetected: "No issues detected in {words}",
  issuesDetected:
    "one:{count} likely issue in {words}|other:{count} likely issues in {words}",
  textToCheck: "Text to check",
  inputPlaceholder: "Paste a paragraph, a page, or a whole paper…",
  tryASample: "Try a sample",
  clear: "Clear",
  results: "Results",
  noRuleBasedIssues:
    "No rule-based issues detected. This checker targets mechanical errors with high confidence — it does not judge style, tone, or complex grammar, so give the draft a careful human read too.",
} as const;

function ConfidenceBadge({
  confidence,
  strings,
}: {
  confidence: GrammarFinding["confidence"];
  strings: typeof GRAMMAR_CHECKER_STRINGS;
}) {
  const label =
    confidence === "high" ? strings.highConfidence : strings.worthALook;
  const cls =
    confidence === "high"
      ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200"
      : "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200";
  return (
    <span className={`rounded-full px-2 py-0.5 font-medium text-xs ${cls}`}>
      {label}
    </span>
  );
}

function CopyFixButton({
  text,
  strings,
}: {
  text: string;
  strings: typeof GRAMMAR_CHECKER_STRINGS;
}) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleCopy() {
    const ok = await writeToClipboard(text);
    if (ok) {
      setCopied(true);
      setFailed(false);
      setTimeout(() => setCopied(false), 1500);
    } else {
      setFailed(true);
    }
  }

  function label(): string {
    if (copied) {
      return strings.copied;
    }
    if (failed) {
      return strings.copyFailed;
    }
    return strings.copyFix;
  }

  return (
    <button
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 font-medium text-foreground text-xs hover:bg-muted"
      onClick={handleCopy}
      type="button"
    >
      {copied ? (
        <Check aria-hidden="true" className="size-3.5" />
      ) : (
        <Copy aria-hidden="true" className="size-3.5" />
      )}
      {label()}
    </button>
  );
}

function FindingCard({
  finding,
  strings,
}: {
  finding: GrammarFinding;
  strings: typeof GRAMMAR_CHECKER_STRINGS;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-foreground text-sm">{finding.title}</p>
        <ConfidenceBadge confidence={finding.confidence} strings={strings} />
      </div>
      <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
        {finding.message}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code className="rounded bg-muted px-2 py-1 text-foreground text-xs">
          {finding.excerpt}
        </code>
        {finding.suggestion ? (
          <>
            <span aria-hidden="true" className="text-muted-foreground text-xs">
              →
            </span>
            <code className="rounded bg-muted px-2 py-1 text-foreground text-xs">
              {finding.suggestion}
            </code>
            <CopyFixButton strings={strings} text={finding.suggestion} />
          </>
        ) : null}
      </div>
    </div>
  );
}

function summaryLine(
  report: GrammarReport,
  strings: typeof GRAMMAR_CHECKER_STRINGS,
  format: (value: number, options?: Intl.NumberFormatOptions) => string,
  locale: string
): string {
  const words = tPlural(strings.wordsCount, locale, report.wordCount, {
    count: format(report.wordCount),
  });
  if (report.findings.length === 0) {
    return t(strings.noIssuesDetected, { words });
  }
  return tPlural(strings.issuesDetected, locale, report.findings.length, {
    words,
  });
}

export default function GrammarCheckerWidget() {
  const [text, setText] = useState("");
  const strings = useToolUi(GRAMMAR_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const report = checkGrammar(text);
  const hasInput = text.trim().length > 0;

  return (
    <>
      {/* Input */}
      <div className="space-y-3">
        <textarea
          aria-label={strings.textToCheck}
          className="min-h-[220px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          onChange={(e) => setText(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={text}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className="rounded-md border border-border bg-background px-3 py-1.5 font-medium text-foreground text-sm hover:bg-muted"
            onClick={() => setText(SAMPLE)}
            type="button"
          >
            {strings.tryASample}
          </button>
          {hasInput ? (
            <button
              className="rounded-md border border-border bg-background px-3 py-1.5 font-medium text-foreground text-sm hover:bg-muted"
              onClick={() => setText("")}
              type="button"
            >
              {strings.clear}
            </button>
          ) : null}
        </div>
      </div>

      {/* Results */}
      {hasInput ? (
        <section aria-live="polite" className="space-y-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-semibold text-foreground text-xl">
              {strings.results}
            </h2>
            <p className="text-muted-foreground text-sm">
              {summaryLine(report, strings, fmt, locale)}
            </p>
          </div>
          {report.findings.length === 0 ? (
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="text-muted-foreground text-sm leading-relaxed">
                {strings.noRuleBasedIssues}
              </p>
            </div>
          ) : (
            report.findings.map((finding) => (
              <FindingCard
                finding={finding}
                key={`${finding.rule}-${finding.index}`}
                strings={strings}
              />
            ))
          )}
        </section>
      ) : null}
    </>
  );
}
